import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { getGeneric } from '../../common/constants'

// NETRA quality band -> MUI palette color + label.
const BAND = {
  EXCELLENT: { color: 'success', label: 'Excellent' },
  GOOD: { color: 'info', label: 'Good' },
  FAIR: { color: 'warning', label: 'Fair' },
  POOR: { color: 'error', label: 'Poor' },
}

// The 5 sub-scores, in display order. `q` maps each raw sub-score to a
// 0-100 "higher = better" quality contribution — the SAME transform the
// scoring engine uses (netra/address/intelligence/composite.py::_compute_score),
// so the bars visibly explain the gauge number.
const SUB_SCORES = [
  {
    key: 'f12_address_completeness',
    label: 'Completeness',
    q: (v) => v, // already 0-100
  },
  {
    key: 'f13_gibberish_score',
    label: 'Text legibility',
    q: (v) => (1 - v) * 100, // gibberish is inverse: 0 gibberish => 100 legible
  },
  {
    key: 'f14_address_delivery_success',
    label: 'Delivery history',
    q: (v) => v * 100,
  },
  {
    key: 'f15_address_match_rate',
    label: 'Locality match',
    q: (v) => v * 100,
    // Needs real NER to be meaningful; "pending" under the v1 stub parser.
    pending: ({ nerIsStub }) => nerIsStub,
  },
  {
    key: 'f32_address_verified_status',
    label: 'Verification',
    q: (v) => ((v + 1) / 2) * 100, // -1..1 => 0..100, 0 (unknown) => 50
    // "pending" until a channel supplies a verification status.
    pending: ({ verificationMissing }) => verificationMissing,
  },
]

// Action-oriented tip text per flag code. Falls back to the API description.
const TIP_TEXT = {
  missing_flat_number: 'Add a flat / house number',
  missing_street: 'Add street / road detail',
  missing_area: 'Add the locality / area name',
  missing_landmark: 'Add a nearby landmark',
  gibberish_in_street: 'Clarify the street text — it looks unclear',
  repetitive_text: 'Remove repeated text from the address',
  keyboard_roll: 'Remove random / repeated characters',
  invalid_pincode_format: 'Fix the pincode — it must be 6 digits',
  pincode_unserviceable: 'Pincode is not serviceable — verify it',
  city_pincode_mismatch: 'City does not match the pincode — verify',
  state_pincode_mismatch: 'State does not match the pincode — verify',
  address_disputed: 'Address reported as disputed — verify with buyer',
  unmatched_area_token: 'Area name not recognised for this pincode',
  address_too_short: 'Address is too short — add more detail',
}

// Bar color by quality value (higher = better).
const barColor = (v) => {
  if (v >= 80) return 'success'
  if (v >= 60) return 'info'
  if (v >= 40) return 'warning'
  return 'error'
}

/**
 * Address Quality card for the admin order detail page.
 *
 * Ported from proship-web; the admin panel ALWAYS shows the NETRA score
 * (it ignores the merchant-level showNetraScoreInMerchantPanel flag).
 *
 * `netra` is the lightweight summary that rides on the order payload
 * (order.netra_address = { score, band, scoredAt }) — renders the gauge
 * + band immediately. Sub-scores + flags are lazy-fetched from the netra_score
 * collection via GET /api/order/{orderId}/netra-score.
 *
 * Renders nothing when there is no score (non-useNetra orders).
 */
const AddressQualityCard = ({ netra, orderId, scorePath, scoredStageNote }) => {
  const [resp, setResp] = useState(null)
  const [tipsOpen, setTipsOpen] = useState(false)

  useEffect(() => {
    let active = true
    if (!orderId || !netra) return undefined
    // Channel orders live at a sibling endpoint; `scorePath` lets the same
    // card serve both without a fork. Defaults to the order endpoint.
    getGeneric(scorePath || `order/${orderId}/netra-score`)
      .then((res) => {
        if (!active) return
        const payload = res?.data?.addressScoreResponse
        if (payload) setResp(payload)
      })
      .catch(() => {
        /* sub-scores/flags are best-effort — gauge + band still render */
      })
    return () => {
      active = false
    }
  }, [orderId, netra, scorePath])

  if (!netra || (netra.score === null && netra.score === undefined)) return null
  const score = Number(netra.score)
  if (Number.isNaN(score)) return null

  const band = BAND[netra.band] || { color: 'inherit', label: netra.band || '—' }

  const subScores = resp?.sub_scores
  const flags = Array.isArray(resp?.flags) ? resp.flags : []
  // Authoritative NER-gate signal from NETRA. `ner_is_real` flips true the moment
  // a trained model is serving, which auto-enables NER-dependent sub-scores (e.g.
  // Locality match) with no client redeploy. Older backends omit it — fall back to
  // sniffing the model tag for "stub".
  const versions = resp?.versions || {}
  const nerIsStub =
    typeof versions.ner_is_real === 'boolean'
      ? !versions.ner_is_real
      : (versions.ner_model || '').toLowerCase().includes('stub')
  const verificationMissing = flags.some((f) => f.code === 'address_unverified')
  const ctx = { nerIsStub, verificationMissing }

  // Actionable tips: warn + critical only (info-severity items are parser
  // limitations under the v1 stub NER, not real address defects).
  const tips = flags
    .filter((f) => f.severity === 'warn' || f.severity === 'critical')
    .map((f) => ({
      code: f.code,
      severity: f.severity,
      text: TIP_TEXT[f.code] || f.description || f.code,
    }))

  return (
    <Card sx={{ boxShadow: 1 }}>
      <CardHeader
        title='Address Quality'
        action={
          <Tooltip title='Address quality scored by NETRA'>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 1, mr: 1 }}>
              Powered by NETRA
            </Typography>
          </Tooltip>
        }
      />
      <CardContent>
        {scoredStageNote && (
          // Channel-order scores are frozen at ingest and never re-derived from the
          // fulfilled shipment, so the same buyer/address can legitimately show a
          // different score on the Orders page. Unlabelled that reads as a bug.
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
            {scoredStageNote}
          </Typography>
        )}
        <Stack direction='row' spacing={3} alignItems='center' sx={{ mb: subScores ? 2.5 : 0 }}>
          {/* circular score gauge */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant='determinate'
              value={100}
              size={92}
              thickness={4}
              sx={{ color: (t) => t.palette.grey[200] }}
            />
            <CircularProgress
              variant='determinate'
              value={Math.max(0, Math.min(100, score))}
              size={92}
              thickness={4}
              color={band.color}
              sx={{ position: 'absolute', left: 0 }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant='h5' fontWeight={700}>
                {Math.round(score)}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                /100
              </Typography>
            </Box>
          </Box>

          <Box>
            <Chip color={band.color} label={band.label} sx={{ fontWeight: 600 }} />
            {netra.scoredAt && (
              <Typography variant='caption' display='block' color='text.secondary' sx={{ mt: 1 }}>
                Scored {new Date(netra.scoredAt).toLocaleString('en-IN')}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* sub-score bars — the "why" behind the gauge. Factors whose signal
            isn't available yet (Locality match under the stub NER, Verification
            with no channel status) are hidden entirely rather than shown as
            "pending"; they reappear automatically once their signal arrives. */}
        {subScores && (
          <Stack spacing={1.25}>
            {SUB_SCORES.filter(
              (s) => !(typeof s.pending === 'function' && s.pending(ctx))
            ).map((s) => {
              const raw = subScores[s.key]
              const value =
                raw === null || raw === undefined ? 0 : Math.max(0, Math.min(100, s.q(raw)))
              return (
                <Box key={s.key}>
                  <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.25 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {s.label}
                    </Typography>
                    <Typography variant='caption' color='text.primary' fontWeight={600}>
                      {Math.round(value)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant='determinate'
                    value={value}
                    color={barColor(value)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: (t) => t.palette.grey[200],
                    }}
                  />
                </Box>
              )
            })}
          </Stack>
        )}

        {/* actionable tips, collapsed by default */}
        {tips.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Link
              component='button'
              type='button'
              variant='body2'
              underline='hover'
              onClick={() => setTipsOpen((o) => !o)}
              sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 600 }}
            >
              {tipsOpen ? '▾' : '▸'}&nbsp;{tips.length} tip{tips.length > 1 ? 's' : ''} to improve
              this address
            </Link>
            <Collapse in={tipsOpen}>
              <List dense disablePadding sx={{ mt: 0.5 }}>
                {tips.map((t) => (
                  <ListItem key={t.code} sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={`• ${t.text}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: t.severity === 'critical' ? 'error.main' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default AddressQualityCard
